import { CmsDisplayTextLabel } from '../../cmsTypes'

export class OccContextPropsApi {
    /**
     * Alt text format for event poster.
     */
    eventPosterAltTextFormat: string = 'Theatrical poster for ${eventName}'
    /**
     * Alt text format for event gallery images
     */
    eventGalleryImageAltTextFormat: string = 'Gallery image for ${eventName}'
    /**
     * Alt text format for film posters
     */
    filmPosterAltTextFormat: string = 'Theatrical poster for ${filmTitle}'
    /**
     * Alt text format for film stills
     */
    filmStillAltTextFormat: string = 'Film still for ${filmTitle}'
    /**
     * Alt text format for film stills
     */
    filmBackdropAltTextFormat: string = 'Film backdrop for ${filmTitle}'
    /**
     * Alt text format for item images.
     */
    itemImageAltTextFormat: string = 'Image for ${itemName}'
    /**
     * Alt text format for Payment Method icons.
     */
    redirectPaymentMethodIconAltTextFormat: string = 'Image for ${paymentMethodName}'
    /**
     * The suffix displayed after an optional form field.
     * Note: Include a whitespace character to ensure that a screen reader identifies the suffix as a separate word.
     */
    formFieldOptionalSuffix: string = ' (optional)'
    /**
     * The name of the cinema chain (e.g. 'ABC Cinemas').
     */
    cinemaChainName: string = 'ABC Cinemas'
    /**
     * The title displayed when there's an unexpected error inside a component.
     */
    componentErrorTitle: string = 'Sorry, something went wrong.'
    /**
     * The name of the icon asset displayed when an unexpected error inside a component.
     */
    componentErrorIcon: string = 'box-with-cross-eyes-and-warning-symbol'
    /**
     * The name of the icon asset displayed when the text in the password input is currently hidden.
     */
    passwordInputHiddenIcon: string = 'eye'
    /**
     * The name of the icon asset displayed when the text in the password input is currently visible.
     */
    passwordInputVisibleIcon: string = 'eye-with-diagonal-line'
    /**
     * The aria-label set on the modal's close button.
     */
    modalCloseButtonAriaLabel: string = 'Close Modal'
    /**
     * The name of the icon asset displayed on a modal's close button.
     */
    modalCloseButtonIcon: string = 'cross'
    /**
     * Message to be displayed when an empty result set is returned from a search.
     */
    noSearchResultsFoundMessage: string = "No results found for '${searchTerm}'"
    /**
     * The name of the icon asset displayed when the rule passes the check successfully.
     */
    passwordRuleCheckerSuccessIconName: string = 'check'
    /**
     * The name of the icon asset displayed when the rule doesn't pass the check successfully.
     */
    passwordRuleCheckerInvalidIconName: string = 'cross'
    /**
     * The name of the icon asset displayed when the rule is in the initial state.
     */
    passwordRuleCheckerInitialIconName: string = 'bullet-point'
    /**
     * The help text displayed for the uppercase letter rule in the password rule checker.
     */
    passwordRuleCheckerUppercaseRule: string = 'Uppercase letter'
    /**
     * The help text displayed for the lowercase letter rule in the password rule checker.
     */
    passwordRuleCheckerLowercaseRule: string = 'Lowercase letter'
    /**
     * The help text displayed for the numeric character rule in the password rule checker.
     */
    passwordRuleCheckerNumericRule: string = 'Numeric character'
    /**
     * The help text displayed for the minimum length rule in the password rule checker.
     */
    passwordRuleCheckerMinLengthRule: string = 'At least 10 characters'
    /**
     * The help text displayed for the special character rule in the password rule checker.
     */
    passwordRuleCheckerSpecialCharacterRule: string = 'At least 1 special character'
    /**
     * Label shown for the password rule checker information message.
     */
    passwordRuleCheckerHelpText: string = 'Password must contain:'
    /**
     * Whether Sunday is considered as the start the week (i.e. whether week starts on Sunday or Monday).
     */
    startWeekOnSunday: boolean = false
    /**
     * The name of the icon asset displayed to signal a loading state.
     */
    loadingIconName: string = 'spinner'
    /**
     * Format string for displaying the image alt text of a member photo.
     */
    memberPhotoAltTextFormat: string = 'Member photo for ${memberName}'
    /**
     * Format string for displaying the image alt text of a member reward.
     */
    memberRewardImageAltTextFormat: string = 'Reward image for ${rewardName}'
    /**
     * The name of the icon asset displayed on the advance booking period notifications.
     */
    advanceBookingPeriodNotificationIconName?: string = 'clock-solid'

    /**
     * Label used for films that belong to [[FilmCategory.NowShowing]].
     */
    nowShowingLabel: string = 'Now Showing'
    /**
     * Label used for films that belong to [[FilmCategory.ComingSoon]].
     */
    comingSoonLabel: string = 'Coming Soon'
    /**
     * Label used for films that belong to [[FilmCategory.AdvanceBooking]].
     */
    advanceBookingLabel: string = 'Advance Bookings'
    /**
     * Label used for films that belong to [[FilmCategory.AdvanceBooking]] and can currently be booked by the patron.
     */
    preBookNowLabel: string = 'Pre-Book Now'
    /**
     * Label used for films that are being sponsored.
     */
    sponsoredLabel: string = 'Sponsored'
    /**
     * The format used for displaying film runtime values.
     */
    filmRuntimeFormat: string = '${hours}h ${minutes}m'
    /**
     * The label shown for the button that adds a film to the watchlist.
     */
    addToWatchlistButtonLabel: string = 'Add to watchlist'
    /**
     * The label shown for the button that removes a film from the watchlist.
     */
    removeFromWatchlistButtonLabel: string = 'Remove from watchlist'
    /**
     * Title text used when a film cannot be added to the watchlist.
     */
    failedToAddFilmToWatchlistTitle: string = 'Failed to add to watchlist'
    /**
     * Message text used when a film cannot be added to the watchlist.
     */
    failedToAddFilmToWatchlistMessage: string =
        'Sorry, ${filmTitle} could not be added to your watchlist. Please try again later.'
    /**
     * Title text used when a film cannot be removed from the watchlist.
     */
    failedToRemoveFilmFromWatchlistTitle: string = 'Failed to remove from watchlist'
    /**
     * Message text used when a film cannot be removed from the watchlist.
     */
    failedToRemoveFilmFromWatchlistMessage: string =
        'Sorry, ${filmTitle} could not be removed from your watchlist. Please try again later.'

    /**
     * The label shown to represent something that is available because exclusively because of a loyalty reward.
     */
    loyaltyRewardExclusiveLabel: string = 'Just for you'
    /**
     * The default label shown for the name of a member's primary balance. When available, the `name` of the relevant [[BalanceType]] will be used instead.
     */
    defaultBalanceTypeName: string = 'Points'
    /**
     * The default label shown for the units of a member's primary balance. When available, the `unitName` of the relevant [[BalanceType]] will be used instead.
     */
    defaultBalanceTypeUnitName: string = 'points'
    /**
     * The format used when displaying member balance units.
     */
    memberBalanceFormat: string = '${units|n} ${unitName}'
    /**
     * The format used when displaying a friend code. Must contain 20 '*' placeholders.
     */
    friendCodeFormat: string = '**** **** **** **** ****'

    /**
     * The currency symbol.
     */
    symbol: string = '$'
    /**
     * The pattern for currency formatting.
     * - '%s' symbol placeholder.
     * - '%v' value placeholder.
     */
    format: string = '%s%v'
    /**
     * The decimal point separator.
     */
    decimalSeparator: string = '.'
    /**
     * The number of decimal places.
     */
    decimalPrecision: number = 2
    /**
     * The thousands separator.
     */
    thousandsSeparator: string = ','
    /**
     * The placeholder value returned when a non-number value is passed in.
     */
    nonNumberPlaceholder: string = ''

    /**
     * Format used to create ISO date strings (e.g. "1973-04-15T23:55:00.123Z").
     */
    iso: string = 'YYYY-MM-DDTHH:mm:ss.SSSZ'
    /**
     * Format used to create long date strings (e.g. "April 15 1973").
     */
    dateLong: string = 'MMMM D YYYY'
    /**
     * Format used to create short date strings (e.g. "04/15/1973").
     */
    dateShort: string = 'MM/DD/YYYY'
    /**
     * Date format to fall back to. This will not be used internally and it is the consumers responsibility to use these defaults.
     */
    dateDefault: string = 'MM/DD/YYYY'
    /**
     * Format used to create long time strings (e.g. "13:55:01").
     */
    timeLong: string = 'HH:mm:ss'
    /**
     * Format used to create short time strings (e.g. "13:55").
     */
    timeShort: string = 'HH:mm'
    /**
     * Time format to fall back to. This will not be used internally and it is the consumers responsibility to use these defaults.
     */
    timeDefault: string = 'HH:mm:ss'

    /**
     * Determines whether or not watchlist functionality will be enabled across components.
     */
    watchlistEnabled: boolean = false
    /**
     * The name of the icon asset to display on the film watchlist button.
     */
    watchlistButtonIconName: string = 'bookmark'

    /**
     * Set ordering mode for components
     */
    isSeatFirstOrdering: boolean
    /**
     * Maximum allowed tickets in an order
     */
    maximumAllowedTicketsInAnOrder: number = 20
    /**
     * Determines the duration in minutes before the start time of a showtime that admission into the cinema is allowed.
     */
    admittingDuration: number = 1
    /**
     * Determines the number of minutes before the start of a showtime after which unpaid bookings are not allowed.
     * It is recommended that this is set to the same value as the `UnpaidBookingCutOffMins` configuration in Vista Cinema.
     */
    unpaidBookingCutoffTime: number = 0
    /**
     * Determines how booking fees are displayed. False for inclusive, true for exclusive.
     */
    bookingFeeExclusive: boolean
    /**
     * Determines if booking fees are added in unpaid booking mode.
     */
    isBookingFeeApplicableToUnpaidBookings: boolean
    /**
     * Determines whether prices are shown inclusive or exclusive of tax. False for inclusive, true for exclusive.
     */
    taxModeExclusive: boolean
    /**
     * Determines the maximum allowed items per order.
     */
    maximumItemsPerOrder: number = 99
    /**
     * Determines the maximum allowed modifiers per item.
     *
     * This configuration is only provided to restrict a large amount of modifiers being added to an item
     * if quantity limit is set to unlimited.
     */
    maximumModifiersPerItem: number = 99
    /**
     * Determines how many days a redemption with an expiry date is considered 'expiring soon' before expiring.
     *
     * Currently only applies to recognition and discount tickets.
     */
    redemptionExpiringSoonDurationInDays: number = 90

    /**
     * The label shown for the booking fee of an order.
     */
    bookingFeeLabel: CmsDisplayTextLabel[] = [{ text: 'Booking fee' }]
    /**
     * The format shown for the booking fee of an order in booking fee inclusive mode.
     */
    includedBookingFeeLabel: CmsDisplayTextLabel[] = [{ text: 'Includes booking fee' }]
    /**
     * Message displayed when the maximum allowed numbers of items have been added to an order.
     */
    itemLimitReachedMessage: string =
        'You have added the maximum number of items that can be added to this order. Please remove existing items from your order to add new items.'
    /**
     * Message displayed when the in-progress items in an order have reached the maximum allowed item count.
     */
    inProgressItemLimitReachedMessage: string = 'You have reached the maximum number of items allowed in an order.'
    /**
     * Message displayed when the in-progress items in an order have reached the maximum allowed redemption limit for a restricted price.
     */
    inProgressItemRedemptionLimitReachedMessage: string =
        'You have reached the maximum number of items available for this price.'
    /**
     * The label shown for the total tax of an order.
     */
    taxLabel: string = 'Tax'
    /**
     * The label shown for the estimated total tax of an order.
     */
    estimatedTaxLabel: string = 'Estimated tax'
    /**
     * The label shown for the subtotal cost of an order, excluding booking fees.
     */
    subtotalCostExcludingBookingFeeLabel: string = 'Subtotal'
    /**
     * The label shown for the subtotal cost of an order, including booking fees.
     * Default: Subtotal (Incl. booking fee of ${bookingFee})
     */
    subtotalCostIncludingBookingFeeLabel: CmsDisplayTextLabel[] = [
        { text: 'Subtotal (Incl. booking fee of ${bookingFee})' },
    ]
    /**
     * The label shown for the total cost of an order.
     */
    totalCostLabel: string = 'Total'
    /**
     * The label shown for the total savings of an order.
     */
    totalSavingsLabel: string = 'Your savings'

    /**
     * The default label shown on the 'all' filter option.
     */
    allOptionLabel: string = 'All'
    /**
     * The label shown for a billing period filter option with a billing period of one month.
     */
    oneMonthSubscriptionBillingPeriodLabel: string = '1 month'
    /**
     * The label shown for a billing period filter option.
     * Default: ${billingPeriodInMonths} months
     */
    subscriptionBillingPeriodLabel: string = '${billingPeriodInMonths} months'
    /**
     * The label shown for a recurring billing recurrence filter option.
     */
    recurringSubscriptionBillingPeriodLabel: string = 'Recurring'
    /**
     * The label shown for a non-recurring billing recurrence filter option.
     */
    nonRecurringSubscriptionBillingPeriodLabel: string = 'Non-recurring'

    /**
     * The label shown when a showtime is sold out.
     */
    soldOutLabel: string = 'SOLD OUT'

    /**
     * Accessibility text format used to describe a normal seat.
     * The token ${formattedSeatLabel} is the label of the seat in the format [Row][Seat], e.g. 'B15'.
     */
    normalSeatAriaLabelFormat: string = 'Normal seat ${formattedSeatLabel}'
    /**
     * Accessibility text format used to describe a wheelchair seat.
     * The token ${formattedSeatLabel} is the label of the seat in the format [Row][Seat], e.g. 'B15'.
     */
    wheelchairSeatAriaLabelFormat: string = 'Wheelchair space ${formattedSeatLabel}'
    /**
     * Accessibility text format used to describe a companion seat.
     * The token ${formattedSeatLabel} is the label of the seat in the format [Row][Seat], e.g. 'B15'.
     */
    companionSeatAriaLabelFormat: string = 'Companion seat ${formattedSeatLabel}'
    /**
     * Accessibility text format used to describe a left sofa seat.
     * The token ${formattedSeatLabel} is the label of the seat in the format [Row][Seat], e.g. 'B15'.
     */
    sofaLeftSeatAriaLabelFormat: string = 'Left sofa ${formattedSeatLabel}'
    /**
     * Accessibility text format used to describe a middle sofa seat.
     * The token ${formattedSeatLabel} is the label of the seat in the format [Row][Seat], e.g. 'B15'.
     */
    sofaMiddleSeatAriaLabelFormat: string = 'Middle sofa ${formattedSeatLabel}'
    /**
     * Accessibility text format used to describe a right sofa seat.
     * The token ${formattedSeatLabel} is the label of the seat in the format [Row][Seat], e.g. 'B15'.
     */
    sofaRightSeatAriaLabelFormat: string = 'Right sofa ${formattedSeatLabel}'
    /**
     * Accessibility text format used to describe an unavailable seat.
     * The token ${formattedSeatLabel} is the label of the seat in the format [Row][Seat], e.g. 'B15'.
     */
    unavailableSeatAriaLabelFormat: string = 'Unavailable seat ${formattedSeatLabel}'
    /**
     * The aria-label for the 'Add to Apple Wallet' button.
     */
    addToAppleWalletButtonAriaLabelFormat: string = 'Add to Apple Wallet'
    /**
     * Format string for displaying the image alt text of a film advertisement.
     * Variable: ${filmTitle}
     */
    filmAdvertisementAltTextFormat: string = 'Film advertisement for ${filmTitle}'
    /**
     * Format string for displaying the image alt text of a film title treatment.
     * Variable: ${filmTitle}
     */
    filmTitleTreatmentAltTextFormat: string = '${filmTitle}'
    /**
     * Format string for displaying the image alt text of a member photo for a member without a name.
     */
    memberPhotoWithoutNameAltTextFormat: string = 'Member photo'
    /**
     * The format of the aria-label for redirect payment method buttons.
     * Default: Pay with ${paymentMethodName}
     */
    redirectPaymentMethodButtonAriaLabelFormat: string = 'Pay with ${paymentMethodName}'
    /**
     * The format of the aria-label for the remove gift card button in the Order Payment Form component.
     * Default: Remove gift card ${maskedCardNumber}
     */
    removeGiftCardButtonAriaLabelFormat: string = 'Remove gift card ${maskedCardNumber}'
    /**
     * The format of the aria-label for the remove member balance payment button in the Order Payment Form component.
     * Default: Remove payment of ${units}
     */
    removeMemberBalancePaymentButtonAriaLabelFormat: string = 'Remove payment of ${units}'
    /**
     * The aria-label for the 'Save to Google Pay' button.
     */
    saveToGooglePayAriaLabelFormat: string = 'Save to Google Pay'
}
